import {prefixWithPipe} from "../prefixWithPipe";
import type {PropertyMap} from "../../../common/utils/objects";
import {VisitRecord} from "./visitRecord";
import {resolve} from "../../../container";
import {Dictionary} from "../../../page/elements/dictionary";
import {html, type TemplateResult} from "lit";
import {property, query, queryAll, state} from "lit/decorators.js";
import {LocatedDates} from "../../../common/utils/dates/locatedDates";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import type {FeedEntry} from "../feedEntry";
import {relativize} from "../../../common/utils/url";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingTeaserInteract} from "../../../tracking/trackingAttributes";
import {handleActivationKey, KEYS} from "../../../common/utils/events";
import {UnLitElement} from "../../../common/elements";
import type {EopNewsSearchDisclaimerDialogs} from "./disclaimerDialogs";
import type {EopNewsSearchLightboxDialogs} from "./lightboxDialogs";
import {PageFeatures} from "../../../page/elements/pageFeatures";

const HOVER_CLASS = "hover";

const TYPE_CLASS: PropertyMap = {
    download: "download-tile",
    default: ""
};

export abstract class SearchTile extends UnLitElement {

    @property()
    protected entry: FeedEntry;
    @property()
    protected groupName: string;
    @state()
    protected boxed = false;

    @queryAll("a")
    private links: NodeListOf<HTMLElement>;
    @query(".tile-content")
    private tileContent: HTMLElement;

    private dictionary: Dictionary;

    @property({attribute: "disclaimer-dialog-id", type: String, reflect: true})
    private disclaimerDialogId: string | undefined;
    private disclaimerDialogs: EopNewsSearchDisclaimerDialogs | null;

    @property({attribute: "lightbox-dialog-id", type: String, reflect: true})
    private lightboxDialogId: string | undefined;
    private lightboxDialogs: EopNewsSearchLightboxDialogs | null;

    protected constructor(
        private locatedDates: LocatedDates = resolve(LocatedDates),
        private visitRecord: VisitRecord = resolve(VisitRecord),
        private pageFeatures: PageFeatures = resolve(PageFeatures)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        if (this.entry.data.disclaimer) {
            this.disclaimerDialogs = this.pageFeatures.findFirst<EopNewsSearchDisclaimerDialogs>("eop-news-search-disclaimer-dialogs");
            this.disclaimerDialogId = this.disclaimerDialogs?.create(
                this.entry.data.disclaimer?.headline,
                this.entry.data.disclaimer?.content,
                this.entry.data.url,
                this.dictionary.translate("MSG_DISCLAIMER_ACCEPT"),
                this.dictionary.translate("MSG_DISCLAIMER_DECLINE")
            );
        }
        if (this.entry.data.lightboxImageUrl) {
            this.lightboxDialogs = this.pageFeatures.findFirst<EopNewsSearchLightboxDialogs>("eop-news-search-lightbox-dialogs");
            this.lightboxDialogId = this.lightboxDialogs?.create(
                this.entry.data.headline,
                this.entry.data.subHeadline,
                this.entry.data.url,
                this.entry.data.lightboxImageUrl,
                this.entry.data.mediaType,
                this.entry.data.imageAltText,
                this.dictionary.translate("MSG_DOWNLOAD_IMAGE")
            );
        }
    }

    public disconnectedCallback(): void {
        if (this.disclaimerDialogs && this.disclaimerDialogId) {
            this.disclaimerDialogs.removeDialog(this.disclaimerDialogId);
        }
        if (this.lightboxDialogs && this.lightboxDialogId) {
            this.lightboxDialogs.removeDialog(this.lightboxDialogId);
        }
        super.disconnectedCallback();
    }

    protected renderTile(): TemplateResult {
        if (this.entry.data.disclaimer || this.entry.data.lightboxImageUrl) {
            return html`
                <div
                        @mouseover=${this.addHoverClass}
                        @mouseout=${this.removeHoverClass}
                        @click=${this.handleTileClick}
                        @keydown=${this.handleTileKeydown}
                        class="tile-content ${this.boxed ? "reverse-background" : ""} ${this.typeClass()}"
                        ?download=${this.isDownload()}
                        ${trackingTeaserInteract(this.headlineText(), this.entry.data.url)}
                        tabindex="0"
                >
                    ${this.renderTileContent()}
                </div>
            `;
        } else {
            return html`
                <eop-background-link
                        href=${this.entry.data.url}
                        @mouseover=${this.addHoverClass}
                        @mouseout=${this.removeHoverClass}
                        @click=${this.handleBackgroundLinkClick}
                        @keydown=${this.handleBackgroundLinkKeydown}
                        class="tile-content ${this.boxed ? "reverse-background" : ""} ${this.typeClass()}"
                        ?download=${this.isDownload()}
                        ${trackingTeaserInteract(this.headlineText(), this.entry.data.url)}
                >
                    ${this.renderTileContent()}
                </eop-background-link>
            `;
        }
    }

    private renderTileContent(): TemplateResult {
        return html`
            <div class="tile-image">
                ${this.renderImageElement()}
            </div>
            <div class="tile-text">
                <div class="tile-top">
                    ${this.renderFileExtension()}
                    <div class="metadata">
                        ${this.renderNewLabel()}
                        ${this.getDate()}${this.getNewsType()}
                    </div>
                    ${this.renderHeadline()}
                    ${this.renderSubHeadline()}
                    ${this.renderFileSize()}
                </div>
                <div class="tile-bottom">
                    ${this.renderTopicLinks()}
                </div>
            </div>
        `;
    }

    protected focusableTile(): HTMLElement {
        return this.tileContent;
    }

    protected lastFocusableLink(): Nullable<HTMLElement> {
        return Array.from(this.links).getLast();
    }

    private handleBackgroundLinkClick(): void {
        this.recordVisit();
    }

    private handleBackgroundLinkKeydown(event: KeyboardEvent): void {
        if (event.key === KEYS.ENTER) {
            this.recordVisit();
        }
    }

    private isDownload(): boolean {
        return this.typeClass() === TYPE_CLASS.download;
    }

    private isNotVisited(): boolean {
        return this.entry.data.isNew && this.entry.recentlyVisited === false;
    }

    protected abstract renderImageElement(): Nullable<TemplateResult>;

    protected abstract renderTopicLinks(): Nullable<TemplateResult>;

    protected abstract showDescription(): boolean;

    private isDownloadTile(): boolean {
        return this.entry.data.contentType === "download";
    }

    protected removeHoverClass(e: Event): void {
        e.stopPropagation();
        this.classList.remove(HOVER_CLASS);
    }

    protected addHoverClass(e: Event): void {
        e.stopPropagation();
        this.classList.add(HOVER_CLASS);
    }

    private renderFileExtension(): Nullable<TemplateResult> {
        if (!this.isDownloadTile()) {
            return null;
        }

        return html`<span class="file-extension reverse-background">${this.entry.data.fileMetadata?.extension}</span>`;
    }

    private renderNewLabel(): Nullable<TemplateResult> {
        if (!this.isNotVisited()) {
            return null;
        }

        return html`<span class="label-new"><eop-msg key="CONTENT_SEARCH_NEW"></eop-msg></span>`;
    }

    private renderFileSize(): Nullable<TemplateResult> {
        if (!this.isDownloadTile()) {
            return null;
        }

        return html`<span class="file-size">(${this.entry.data.fileMetadata?.size})</span>`;
    }

    private renderHeadline(): Nullable<TemplateResult> {
        if (this.entry.data.lightboxImageUrl && !this.entry.data.disclaimer) {
            return html`
                <a @click=${this.handleDownloadClick} @keydown=${this.handleDownloadKeydown} download class="message-headline" target="_blank"
                   href=${relativize(this.entry.data.url)}>
                    <div class="headline-text">${unsafeHTML(this.headlineText())}</div>
                </a>`;
        }
        return html`
            <div class="message-headline">
                <div class="headline-text">${unsafeHTML(this.headlineText())}</div>
            </div>`;
    }

    protected abstract headlineText(): string;

    private renderSubHeadline(): Nullable<TemplateResult> {
        if (!this.showDescription()) {
            return null;
        }
        return html`
            <div class="message-description">${unsafeHTML(this.subHeadlineText())}</div>`;
    }

    protected abstract subHeadlineText(): string;

    private handleDownloadClick(event: Event): void {
        event.stopPropagation();
    }

    private handleDownloadKeydown(event: KeyboardEvent): void {
        if (event.key === KEYS.ENTER) {
            event.stopPropagation(); // do not open lightbox/disclaimer dialog
        }
    }

    private handleTileKeydown(event: KeyboardEvent): void {
        handleActivationKey(event, () => this.handleTileClick(event));
    }

    private handleTileClick(event: Event): void {
        if (this.entry.data.disclaimer) {
            if (this.disclaimerDialogs && this.disclaimerDialogId !== undefined) {
                event.preventDefault();
                this.disclaimerDialogs.open(this.disclaimerDialogId);
            }
        } else if (this.entry.data.lightboxImageUrl) {
            if (this.lightboxDialogs && this.lightboxDialogId !== undefined) {
                event.preventDefault();
                this.lightboxDialogs.open(this.lightboxDialogId);
            }
        }
        this.recordVisit();
    }

    private recordVisit(): void {
        if (this.isNotVisited()) {
            this.visitRecord.addVisitedURL(this.entry.data.url);
        }
    }

    private typeClass(): string {
        return TYPE_CLASS[this.entry.data.contentType] ?? TYPE_CLASS.default;
    }

    private getDate(): string {
        return this.locatedDates.toLocalDateString(new Date(this.entry.data.modifiedDate));
    }

    private getNewsType(): string {
        return prefixWithPipe(this.entry.data.newsType);
    }
}